// pages/f&b/cafeteria/index.jsx
"use client";

import { useEffect, useState } from "react";
import Header from "../../../components/layout/header";
import Footer from "../../../components/layout/footer";

import StoreCard from "../StoreCard";
import ProductCard from "../ProductCard";
import StoreModal from "../StoreModal";

import { getStores, getStoreProducts } from "../../../lib/products";
import { loadCart, cartCount as countCart, cartTotal as totalCart } from "../../../lib/cart";

export default function CafeteriaPage() {
  const TYPE = "cafeteria";
  const STORES_PER_PAGE = 6;
  const PRODUCT_LIMIT = 6;

  // stores
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");
  const [storePage, setStorePage] = useState(1);

  // active store products
  const [activeStore, setActiveStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [storeMeta, setStoreMeta] = useState({
    limit: PRODUCT_LIMIT,
    next_cursor: null,
    has_more: false,
  });
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeProductSearch, setStoreProductSearch] = useState("");

  // modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [modalLink, setModalLink] = useState("");
  const [modalStoreName, setModalStoreName] = useState("");

  // cart
  const [cart, setCart] = useState([]);

  // ---------------------------
  // CART SYNC
  // ---------------------------
  useEffect(() => {
    const updateCart = () => setCart(loadCart());

    updateCart();
    window.addEventListener("cartUpdated", updateCart);

    return () => window.removeEventListener("cartUpdated", updateCart);
  }, []);

  // ---------------------------
  // FETCH STORES
  // ---------------------------
  const fetchStores = async () => {
    try {
      setLoadingStores(true);

      const data = await getStores(TYPE);
      setStores(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStores(false);
    }
  };

  useEffect(() => {
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------
  // STORE FILTER + PAGINATION
  // ---------------------------
  const filteredStores = stores.filter((s) => {
    const name = s.storeName || s.name || "";
    return name.toLowerCase().includes(storeSearch.toLowerCase());
  });

  const storePages = Math.ceil(filteredStores.length / STORES_PER_PAGE) || 1;

  const pagedStores = filteredStores.slice(
    (storePage - 1) * STORES_PER_PAGE,
    storePage * STORES_PER_PAGE
  );

  // ---------------------------
  // LOAD STORE PRODUCTS
  // ---------------------------
  const loadStoreProducts = async (storeName, cursor = null, search = "", append = false) => {
    try {
      setStoreLoading(true);

      const json = await getStoreProducts(storeName, TYPE, cursor, search, PRODUCT_LIMIT);

      const list = json?.data || [];
      const m = json?.meta || {};

      if (append) {
        setProducts((prev) => [...prev, ...list]);
      } else {
        setProducts(list);
      }

      setStoreMeta({
        limit: PRODUCT_LIMIT,
        next_cursor: m.next_cursor ?? null,
        has_more: Boolean(m.has_more),
      });
    } catch (err) {
      console.error(err);
    } finally {
      setStoreLoading(false);
    }
  };

  // ---------------------------
  // OPEN / CLOSE STORE
  // ---------------------------
  const openStore = async (store) => {
    const name = store.storeName || store.name;

    setActiveStore({ ...store, name });
    setProducts([]);
    setStoreProductSearch("");
    setStoreMeta({
      limit: PRODUCT_LIMIT,
      next_cursor: null,
      has_more: false,
    });

    await loadStoreProducts(name);

    document.getElementById("store-products-section")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const closeStore = () => {
    setActiveStore(null);
    setProducts([]);

    document.getElementById("stores")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  // ---------------------------
  // MODAL
  // ---------------------------
  const openLinkModal = (store, url) => {
    if (!url) return;

    setModalStoreName(store.storeName || store.name || "");
    setModalLink(url);
    setShowLinkModal(true);
  };

  // ---------------------------
  // CART SUMMARY
  // ---------------------------
  const cartQty = countCart(cart);
  const cartSum = totalCart(cart);

  return (
    <div>
      <Header />

      {/* ======================
          STORES
      ====================== */}
      <section id="stores" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-3xl font-semibold text-[#003B5C]">Cafeteria</h2>

            <p className="mt-2 text-zinc-600">
              Still confused about choosing the food menu you want? Don&apos;t worry. We have several curated cafeterias
              and food options for you. You can view menu highlights, most-loved items, and how to reach each food stall.
            </p>

            {/* SEARCH STORE */}
            <div className="mt-4 max-w-md">
              <input
                type="text"
                placeholder="Search stores..."
                className="w-full p-2 border rounded text-sm"
                value={storeSearch}
                onChange={(e) => {
                  setStoreSearch(e.target.value);
                  setStorePage(1);
                }}
              />
            </div>
          </div>

          {loadingStores && <p>Loading stores...</p>}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 items-stretch">
            {pagedStores.map((store, i) => (
              <StoreCard key={i} store={store} onClick={() => openStore(store)} />
            ))}
          </div>

          {/* PAGINATION */}
          <div className="flex justify-center items-center gap-4 mt-10 text-sm">
            <button
              disabled={storePage <= 1}
              onClick={() => setStorePage((p) => p - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>

            <span>
              {storePage} / {storePages}
            </span>

            <button
              disabled={storePage >= storePages}
              onClick={() => setStorePage((p) => p + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {/* ======================
          STORE PRODUCTS
      ====================== */}
      {activeStore && (
        <section id="store-products-section" className="py-20 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex flex-col md:flex-row md:justify-between gap-4">
              <div>
                <button
                  onClick={closeStore}
                  className="text-xs mb-2 text-zinc-500 hover:text-zinc-700"
                >
                  ← Back to all stores
                </button>

                <h2 className="text-2xl sm:text-3xl font-semibold">{activeStore.name}</h2>

                {activeStore.storeDescription && (
                  <p className="mt-2 text-sm text-zinc-600 max-w-2xl">{activeStore.storeDescription}</p>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs sm:text-sm text-zinc-600">
                  {activeStore.phone && (
                    <>
                      <span>
                        Tel:{" "}
                        <a href={`tel:${activeStore.phone}`} className="font-medium text-[#00778B]">
                          {activeStore.phone}
                        </a>
                      </span>

                      <span className="hidden sm:inline text-zinc-400">•</span>
                    </>
                  )}

                  {activeStore.phone && (
                    <a
                      href={`https://wa.me/${String(activeStore.phone).replace(/[^0-9]/g, "").replace(/^0/, "62")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-700"
                    >
                      Chat via WhatsApp
                    </a>
                  )}

                  {(activeStore.link || activeStore.url || activeStore.website) && (
                    <>
                      <span className="hidden sm:inline text-zinc-400">•</span>

                      <button
                        onClick={() =>
                          openLinkModal(
                            activeStore,
                            activeStore.link || activeStore.url || activeStore.website
                          )
                        }
                        className="px-3 py-1.5 text-xs rounded-full border border-[#00778B] text-[#00778B] hover:bg-[#00778B] hover:text-white transition"
                      >
                        Show Catalog
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* SEARCH PRODUCT */}
              <div className="w-full md:w-72">
                <label className="block text-xs font-medium mb-1">Search products</label>

                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full p-2 border rounded text-sm"
                  value={storeProductSearch}
                  onChange={async (e) => {
                    const val = e.target.value;
                    setStoreProductSearch(val);

                    await loadStoreProducts(activeStore.name, null, val, false);
                  }}
                />
              </div>
            </div>

            {/* PRODUCT GRID */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
                <ProductCard
                key={product.id}
                product={product}
                href={`/product/${product.id}`}   // ✅ ini yang nyambung ke pages/product/[id].jsx
                />
            ))}
            </div>

            {/* LOAD MORE */}
            {storeMeta.has_more && (
              <div className="mt-10 flex justify-center">
                <button
                  disabled={storeLoading}
                  onClick={() =>
                    loadStoreProducts(activeStore.name, storeMeta.next_cursor, storeProductSearch, true)
                  }
                  className="px-4 py-2 border rounded disabled:opacity-50"
                >
                  {storeLoading ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* STORE MODAL */}
      <StoreModal
        open={showLinkModal}
        link={modalLink}
        storeName={modalStoreName}
        onClose={() => setShowLinkModal(false)}
      />

      {/* CART WIDGET */}
      {cart.length > 0 && (
        <div className="fixed bottom-4 right-4 z-40 bg-white shadow-lg border rounded-xl p-3 w-72">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-semibold">
              Cart ({cartQty} item{cartQty > 1 ? "s" : ""})
            </span>

            <span className="text-xs text-zinc-500">
              Rp{cartSum.toLocaleString("id-ID")}
            </span>
          </div>

          <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
            {cart.map((item) => (
              <div key={item.id} className="flex justify-between border-b pb-1">
                <div>
                  <div className="font-medium line-clamp-1">{item.name}</div>
                  <div className="text-zinc-500">
                    {item.quantity} × Rp{Number(item.price || 0).toLocaleString("id-ID")}
                  </div>
                </div>

                <div>
                  Rp{(Number(item.quantity || 0) * Number(item.price || 0)).toLocaleString("id-ID")}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => (window.location = "/cart")}
            className="mt-2 w-full text-xs font-semibold py-1.5 rounded-full bg-[#003B5C] text-white"
          >
            Review Order
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}